import { Component, OnInit } from '@angular/core';
import { ToggleDetailsUseCase } from '@/core/domain/reservation/usecases/details.usecase';
import { ToggleInsuranceUseCase } from '@/core/domain/reservation/usecases/insurance.usecase';
import { Insurance } from '@/core/domain/reservation/models/insurance.model';
import { Details } from '@/core/domain/reservation/models/details.model';
import { ReservationDataService } from '@/common/services/details/details.service';
import { DetailsInterface } from '@/core/domain/details/models/details.model';
import { AlertService } from '@/common/services/alert/alert.service';
import { catchError, throwError } from 'rxjs';
import { UrlService } from '@/common/services/url/url.service';
import { Router } from '@angular/router';
import { isLiteReservation } from '@/common/utils/flow/type-flow.util';

@Component({
    selector: 'fe-reservation-details',
    templateUrl: './reservation-details.component.html',
    styleUrls: ['./reservation-details.component.scss'],
})
export class ReservationDetailsComponent implements OnInit {
    reservationDetails: DetailsInterface | null = null;
    isDetails?: boolean;
    isInsurance?: boolean;

    openedInsurances: boolean[] = [];

    constructor(
        private toggleInsuranceUseCase: ToggleInsuranceUseCase,
        private toggleDetailsUseCase: ToggleDetailsUseCase,
        private reservationDataService: ReservationDataService,
        private alert: AlertService,
        private url: UrlService,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.reservationDataService
            .getDetails()
            .pipe(
                catchError((error) => {
                    this.alert.openDefault(error).subscribe(() => {
                        this.router.navigateByUrl(this.url.format(''));
                    });
                    return throwError(() => error);
                }),
            )
            .subscribe({
                next: (details) => {
                    this.reservationDetails = details;
                    this.openedInsurances = new Array(
                        details?.data?.reservationDetails?.insurance?.length,
                    ).fill(false);
                },
            });
    }

    private get details() {
        return this.reservationDetails?.data?.reservationDetails;
    }

    get isInternational(): boolean {
        return !!this.details && this.details.currency !== 'MXN';
    }

    get isPrePaid(): boolean {
        return this.details?.paymentStatus === 'PAID';
    }

    get isLite(): boolean {
        if (!this.details || this.isInternational) {
            return false;
        }
        return isLiteReservation(
            this.details.typeFlow,
            (this.details.insurance ?? []).length,
        );
    }

    getPriceLabelKey(): string {
        const base = 'fe_details_step5_reservation_details_price_details_right';
        if (this.isInternational) {
            return base;
        }
        if (this.isPrePaid) {
            return base + '_prepaid';
        }
        return this.isLite ? base + '_lite' : base;
    }

    get hasProducts(): boolean {
        return (
            (this.reservationDetails?.data?.reservationDetails?.products
                ?.length ?? 0) > 0
        );
    }

    toggleInsurance(index: number): void {
        this.openedInsurances[index] = !this.openedInsurances[index];
    }

    isInsuranceOpen(index: number): boolean {
        return this.openedInsurances[index];
    }

    toggleDetails(): void {
        this.toggleDetailsUseCase
            .execute()
            .subscribe(
                (details: Details) => (this.isDetails = details.isDetails),
            );
    }
}
